import type { UIStateAPI } from "@/types";
import { zAudioMuted } from "@/data/schemas";

interface AudioAdapterDeps {
  readonly store: any;
  readonly uiState: UIStateAPI;
  readonly eventBus: any;
  readonly getPath: (key: string) => string | null;
  readonly persist: () => void;
}

export const hydrateAudioFromSession = (
  deps: Pick<AudioAdapterDeps, "store" | "getPath">,
  screen: Record<string, unknown> | null | undefined
) => {
  if (!screen) return;
  const { store, getPath } = deps;
  const aPath = getPath("audioMuted");
  if (!aPath) return;
  try {
    store.registerSchema(aPath, zAudioMuted);
  } catch {}
  if (typeof (screen as any).audioMuted === "boolean") {
    store.set(aPath, !!(screen as any).audioMuted, { source: "hydration" });
  }
};

export const registerAudioAdapter = (deps: AudioAdapterDeps): (() => void) => {
  const { store, uiState, eventBus, getPath, persist } = deps;
  const unsubscribes: (() => void)[] = [];

  // Debug toggles
  const readDebugEnabled = (): boolean => {
    try {
      const d =
        (globalThis as any)?.localStorage?.getItem?.("debug.adapters") ||
        (globalThis as any)?.sessionStorage?.getItem?.("debug.adapters") ||
        (globalThis as any)?.__debugAdapters ||
        "";
      if (typeof d !== "string") return !!(d as any)?.audio || d === "*";
      const parts = d.split(",").map((s: string) => s.trim());
      return parts.includes("*") || parts.includes("audio");
    } catch {
      return false;
    }
  };
  let debugEnabled = readDebugEnabled();
  const debug = (...args: unknown[]) => {
    if (debugEnabled) {
      try {
        // eslint-disable-next-line no-console
        console.debug("[adapter:audio]", ...args);
      } catch {}
    }
  };
  try {
    const unsubDbg = eventBus.on(
      "debug:adapter",
      (payload: { name?: string; enabled?: boolean } | undefined) => {
        try {
          if (!payload) return;
          if (payload.name === "audio") debugEnabled = !!payload.enabled;
          if (payload.name === "*" && typeof payload.enabled === "boolean")
            debugEnabled = !!payload.enabled;
        } catch {}
      }
    );
    unsubscribes.push(unsubDbg);
  } catch {}

  // Store -> UIState
  const unsubscribeStoreAudio = store.subscribe(
    (path: string) => path.endsWith(".screenState.audioMuted"),
    ({ value, source }: { value: unknown; source?: string }) => {
      const isHydration = source === "hydration";
      try {
        (uiState as any).setAudioMuted(!!value);
        debug("store→UI audioMuted", { value, source });
      } catch {}
      if (!isHydration && source !== "socket") persist();
    },
    { debounceMs: 50 }
  );
  unsubscribes.push(unsubscribeStoreAudio);

  // UIState -> Store
  const unsubscribeUIStateChanged = eventBus.on(
    "ui-state-changed",
    (data: any) => {
      try {
        const muted = data?.state?.audio_muted;
        const aPath = getPath("audioMuted");
        if (typeof muted === "boolean" && aPath) {
          try {
            store.registerSchema(aPath, zAudioMuted);
          } catch {}
          debug("UI→store audioMuted", { muted });
          store.set(aPath, muted, { source: "audioAdapter" });
        }
      } catch {}
    }
  );
  unsubscribes.push(unsubscribeUIStateChanged);

  const cleanup = () => {
    for (const u of unsubscribes) {
      try {
        u();
      } catch {}
    }
  };
  try {
    const unsub = eventBus.on("app:destroy", () => cleanup());
    unsubscribes.push(unsub);
  } catch {}
  return cleanup;
};
